<template>
    <div v-if="user" id="licenses-view-container" class="h-full w-full" style="margin-top: 2.5rem;">
        <div v-if="!httpGetLicensesLoading && !httpGetUsersLoading">
            <div class="w-full h-full m-0 p-0">
                <div class="licenses-table mt-0 w-full">
                    <LicensesContent ref="licensesContent" @refresh-licenses-table="refreshData"/>
                </div>
            </div>
        </div>
        <div v-else style="width: 100%; height: 100%;" class="flex justify-content-center align-items-center">
            <LoadingSpinner/>
        </div>
        
    </div>
    <div v-else style="margin-top: 2.5rem;">
        <span>You must sign in to have access to the dashboard with your licenses.</span>
    </div>
</template>

<script setup>
// --- IMPORTS ---
import { ref, watchEffect, onMounted, computed } from 'vue';
// vue
import LicensesContent from '@/components/Licenses/LicensesContent.vue';
import LoadingSpinner from '@/components/Modules/LoadingSpinner.vue';
// Others
import { useRouter } from 'vue-router'
import { useStore } from 'vuex';
import UserUtils from '@/composables/UserUtils';

const router = useRouter()
const store = useStore()
const { logoutUser } = UserUtils(store, router)

// Toast
import toastService from '@/composables/toastService';

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchGetLicenses, fetchGetUsers } = useApiRequest()
const httpGetLicensesLoading = ref(false)
const httpGetLicensesData = ref(null)
const httpGetUsersLoading = ref(false)
const httpGetUsersData = ref(null)

// --- User info ---
const user = computed(() => store.state.user.user)

// REDIRECT IF NOT CONNECTED
watchEffect(() => {
    if (!user.value) { router.push({ path: '/login' }) }
})

// --- Licenses : Load the licenses from the database  ---
const loadLicenses = async () => {
    httpGetLicensesLoading.value = true
    try {
        httpGetLicensesData.value = await fetchGetLicenses()
        store.commit('licenses/setLicenses', httpGetLicensesData.value.licenses)
    } catch (err) {
        handleGetLicensesError(err)
    } finally {
        httpGetLicensesLoading.value = false
    }
}
// Handle an error from the server
const handleGetLicensesError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Get Licenses: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (errorStatus) {
            case 400: // ACCESS_TOKEN_MISSING (400)
                if (errorCode == 'ACCESS_TOKEN_MISSING') {
                    toastService.add({ severity: 'error', summary: "Get Licenses: Authorization Issue.", detail: 'You are not logged in.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else {  toastService.add({ severity: 'error', summary: `Get Licenses: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 401: // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID (401)
                if (errorCode == 'ACCESS_TOKEN_EXPIRED' || errorCode == 'ACCESS_TOKEN_INVALID') {
                    // Logout: Do nothing, the interceptor should have made a refresh token request!
                    // logoutUser()
                }
                else {  toastService.add({ severity: 'error', summary: `Get Licenses: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 404: // Account not found: account has disappear : ACCOUNT_NOT_FOUND (404) => toast + logout
                if (errorCode == 'ACCOUNT_NOT_FOUND') {
                    toastService.add({ severity: 'error', summary: "Get Licenses: Account Not Found.", detail: 'The account no more exists.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else {  toastService.add({ severity: 'error', summary: `Get Licenses: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 500: // DATABASE_ERROR / INTERNAL_ERROR (500) => toast
                if (errorCode == 'DATABASE_ERROR' || errorCode == 'INTERNAL_ERROR') {
                    toastService.add({ severity: 'error', summary: "Get Licenses: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Get Licenses: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Get Licenses: Error ${errorStatus}.`, detail: errorMessage, life: 5000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Get Licenses: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}
// --- Users : Load the licenses from the database  ---
const loadUsers = async () => {
    httpGetUsersLoading.value = true
    try {
        httpGetUsersData.value = await fetchGetUsers()
        store.commit('users/setUsers', httpGetUsersData.value.users)
    } catch (err) {
        handleGetUsersError(err)
    } finally {
        httpGetUsersLoading.value = false
    }
}
// Handle an error from the server
const handleGetUsersError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Get Users: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (errorStatus) {
            case 400: // ACCESS_TOKEN_MISSING (400)
                if (errorCode == 'ACCESS_TOKEN_MISSING') {
                    toastService.add({ severity: 'error', summary: "Get Users: Authorization Issue.", detail: 'You are not logged in.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else { toastService.add({ severity: 'error', summary: `Get Users: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 401: // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID (401)
                if (errorCode == 'ACCESS_TOKEN_EXPIRED' || errorCode == 'ACCESS_TOKEN_INVALID') {
                    // Logout: Do nothing, the interceptor should have made a refresh token request!
                    // logoutUser()
                    break;
                }
                else { toastService.add({ severity: 'error', summary: `Get Users: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 404: // Account not found: account has disappear : ACCOUNT_NOT_FOUND (404) => toast + logout
                if (errorCode == 'ACCOUNT_NOT_FOUND') {
                    toastService.add({ severity: 'error', summary: "Get Users: Account Not Found.", detail: 'The account no more exists.', life: 5000 });
                    // Logout
                    logoutUser()
                    break;
                }
                else { toastService.add({ severity: 'error', summary: `Get Users: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 500: // DATABASE_ERROR / INTERNAL_ERROR (500) => toast (useless)
                if (errorCode == 'DATABASE_ERROR' || errorCode == 'INTERNAL_ERROR') {
                    toastService.add({ severity: 'error', summary: "Get Users: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
                    break;
                }
                else { toastService.add({ severity: 'error', summary: `Get Users: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Get Users: Error ${errorStatus}.`, detail: errorMessage, life: 5000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Get Users: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}

// --- Refresh licenses ---
const refreshData = async () => {
    // Initialize loading
    httpGetLicensesLoading.value = true
    httpGetUsersLoading.value = true
    store.commit('licenses/clearLicenses')
    store.commit('users/clearUsers')
    // Load licenses and users info
    if (user.value) { await loadLicenses(); }
    if (user.value) { await loadUsers(); }
}
onMounted(() => {
    if (user.value) {
        refreshData()
    }
})
</script>

<style scoped>
</style>